import "./bootstrap";

import { createApp } from "vue";
import { createI18n } from "vue-i18n";
import { createGtag } from "vue-gtag";
import VueGoogleMaps from "@fawmi/vue-google-maps";
import { install as VueRecaptchaPlugin } from "vue3-recaptcha-v2";

import isValidUrl from "is-url";
import parseBitbucketUrl from "parse-bitbucket-url";
import parseGithubUrl from "parse-github-url";

import { reactive } from "vue";
import AppVue from "./components/web/App.vue";
import AppRouter from "./web-router.js";

// Import translation files
import bg from "./locales/bg.json";
import en from "./locales/en.json";

// Detect locale from URL or localStorage
function detectLocale() {
  // Check URL path first (e.g., /bg/home or /en/home)
  // Account for base path in production deployments
  let pathname = window.location.pathname;

  // Remove base path if present (e.g., /devspace/dimitar.rekinov/imedia/public/)
  const basePath = import.meta.env.VITE_PUBLIC_PATH || "/";
  if (basePath !== "/" && pathname.startsWith(basePath)) {
    pathname = pathname.substring(basePath.length);
  }

  // Now check the first segment of the remaining path
  const pathSegments = pathname.split("/").filter(Boolean);
  const firstSegment = pathSegments[0];

  if (firstSegment === "bg" || firstSegment === "en") {
    // Save to localStorage for persistence
    localStorage.setItem("locale", firstSegment);
    return firstSegment;
  }

  // Check localStorage
  const stored = localStorage.getItem("locale");
  if (stored && (stored === "bg" || stored === "en")) {
    return stored;
  }

  // Check browser language
  const browserLang = navigator.language || navigator.userLanguage;
  if (browserLang && browserLang.toLowerCase().startsWith("en")) {
    return "en";
  }

  // Default to Bulgarian
  return "bg";
}

// Create i18n instance
const i18n = createI18n({
  legacy: false, // Use Composition API mode
  locale: detectLocale(),
  fallbackLocale: "bg",
  messages: {
    bg,
    en,
  },
});

const app = createApp(AppVue);

// Use i18n
app.use(i18n);

// Use router
app.use(AppRouter);

// Store router instance for global access
const routerInstance = AppRouter;

// Use Google Maps
app.use(VueGoogleMaps, {
  load: {
    key: "AIzaSyAXgBSN80M4ZtklxM1dSzqpqN2nV18dxqc",
  },
});

// Use reCAPTCHA - sitekey from environment variable
app.use(VueRecaptchaPlugin, {
  sitekey: import.meta.env.VITE_RECAPTCHA_SITEKEY || "6LeL8EEUAAAAAFsMIpbHNjtrrxSD9vyAka83H3nS",
});

// Global reCAPTCHA callback function
window.vueRecaptchaApiLoaded = function () {
  // reCAPTCHA API loaded callback
};

// Use Google Analytics if in production
if (window.app_env === "production") {
  app.use(
    createGtag({
      tagId: "UA-114139848-1",
      pageTracker: { router: AppRouter },
    })
  );
}

// Global properties and methods
app.config.globalProperties.$jobApplication = {
  open: "",
};

app.config.globalProperties.$request = null;
// API URI now includes locale (e.g., /api/bg/web or /api/en/web)
app.config.globalProperties.$requestUri =
  import.meta.env.VITE_ASSET_URL + "/api/" + i18n.global.locale.value + "/web";
app.config.globalProperties.$requestTimeout = 12000;
app.config.globalProperties.$assets = {
  countryCode: "bg", // Default to Bulgaria
  baseUrl: import.meta.env.VITE_ASSET_URL || "",
};

// Helper for image paths - automatically prepends base URL and /build/images/ prefix
// Images are copied to /build/images/ by vite-plugin-static-copy during build
app.config.globalProperties.$img = function (path) {
  // Remove leading slash if present
  const cleanPath = path.startsWith("/") ? path.substring(1) : path;
  // Remove 'images/' prefix if present (we'll add it back with full path)
  const imgName = cleanPath.startsWith("images/") ? cleanPath.substring(7) : cleanPath;
  // Return path relative to document root: /build/images/...
  // This works for both production builds and dev mode since images are in /build/images/
  return "/build/images/" + imgName;
};

// Helper to get current locale
app.config.globalProperties.$locale = function () {
  return i18n.global.locale.value;
};

// Helper to change locale
app.config.globalProperties.$changeLocale = function (newLocale) {
  if (newLocale === "bg" || newLocale === "en") {
    i18n.global.locale.value = newLocale;
    localStorage.setItem("locale", newLocale);
    // Update API URI with new locale
    this.$requestUri = import.meta.env.VITE_ASSET_URL + "/api/" + newLocale + "/web";
  }
};

// Breadcrumb data now uses i18n keys
app.config.globalProperties.$breadcrumbData = {
  "/training-and-mentoring-program": {
    a: "breadcrumbs.work_training",
    b: "breadcrumbs.training_program",
  },
  "/services": {
    a: "breadcrumbs.home",
    b: "breadcrumbs.services",
  },
  "/self-learning-resources": {
    a: "breadcrumbs.work_training",
    b: "breadcrumbs.self_learning",
  },
  "/self-learning-resources/:ids": {
    a: "breadcrumbs.work_training",
    b: "breadcrumbs.self_learning",
  },
  "/projects": {
    a: "breadcrumbs.partnerships",
    b: "breadcrumbs.projects",
  },
  "/partnership-programmer": {
    a: "breadcrumbs.partnerships",
    b: "breadcrumbs.partnership_programmer",
  },
  "/partnership-entrepreneur": {
    a: "breadcrumbs.partnerships",
    b: "breadcrumbs.partnership_entrepreneur",
  },
  "/job-application": {
    a: "breadcrumbs.work_training",
    b: "breadcrumbs.free_positions",
  },
  "/refunds-policy": {
    a: "breadcrumbs.home",
    b: "breadcrumbs.refunds",
  },
  "/delivery-policy": {
    a: "breadcrumbs.home",
    b: "breadcrumbs.delivery",
  },
  "/contact-us": {
    a: "breadcrumbs.home",
    b: "breadcrumbs.contact",
  },
  "/clients": {
    a: "breadcrumbs.home",
    b: "breadcrumbs.clients",
  },
  "/home": false,
  "/": false,
};

// Message collection now uses i18n keys
app.config.globalProperties.$messageCollection = {
  invalid_email: "messages.invalid_email",
  invalid_git_link: "messages.invalid_git_link",
  recaptcha: "messages.recaptcha_required",
  email: "validation.email",
  min: "validation.min",
  max: "validation.max",
  string: "validation.string",
  required: "validation.required",
  file: "validation.file",
  file_name: "validation.file_name",
  contact_us_sent: "messages.contact_us_sent",
  job_app_sent: "messages.job_app_sent",
};

// Global methods
app.config.globalProperties.contactUsEmail = function () {
  return [
    '<a href="',
    "mailto:",
    "sales",
    "@",
    "imedia",
    ".bg",
    '">sales',
    "@",
    "imedia.",
    "bg</a>",
  ].join("");
};

app.config.globalProperties.contactUsEmailRaw = function () {
  return ["sales", "@", "imedia", ".bg"].join("");
};

app.config.globalProperties.avatarName = function (id) {
  let botRegex = /bot|googlebot|crawler|spider|robot|crawling/i;
  let isBot = navigator.userAgent && botRegex.test(navigator.userAgent);
  if (!isBot) {
    return i18n.global.t(`team.member_${id}_first`);
  }
  return "";
};

app.config.globalProperties.avatarFullName = function (id) {
  let botRegex = /bot|googlebot|crawler|spider|robot|crawling/i;
  let isBot = navigator.userAgent && botRegex.test(navigator.userAgent);
  if (!isBot) {
    return i18n.global.t(`team.member_${id}_full`);
  }
  return "";
};

app.config.globalProperties.lvContent = function (id) {
  let botRegex = /bot|googlebot|crawler|spider|robot|crawling/i;
  let isBot = navigator.userAgent && botRegex.test(navigator.userAgent);
  if (!isBot) {
    switch (id) {
      case 0:
        return `<h5 class="mb-0">${i18n.global.t("projects.landlord_vision_title")}</h5><p><a href="https://www.landlordvision.co.uk/" target="_blank" rel="nofollow">www.landlordvision.co.uk</a></p>`;
      case 1:
        return `<p>${i18n.global.t("projects.landlord_vision_description")}</p>`;
    }
  }
  return "";
};

app.config.globalProperties.getRoot = function () {
  return this.$root;
};

app.config.globalProperties.breadcrumbHandler = function (urlPath) {
  if (typeof this.$breadcrumbData[urlPath] !== "undefined") {
    // Use nextTick to ensure DOM is ready and refs are available
    this.$nextTick(() => {
      const root = this.getRoot();

      // Vue 3 fix: Check for direct header component reference
      const headerComponent = root?.$headerComponent;
      if (!headerComponent) {
        return; // Header not ready yet, skip breadcrumb update
      }

      if (this.$breadcrumbData[urlPath] === false) {
        headerComponent.hideBreadcrumb();
      } else {
        this.updateBreadcrumb(
          this.$breadcrumbData[urlPath]["a"],
          this.$breadcrumbData[urlPath]["b"]
        );
      }
    });
  }
};

app.config.globalProperties.updateBreadcrumb = function (a, b) {
  const root = this.getRoot();

  // Vue 3 fix: Access header component directly via $headerComponent
  // (registered by HeaderWrapper on mount)
  const headerComponent = root?.$headerComponent;

  if (headerComponent) {
    headerComponent.updateBreadcrumb(a, b);
    headerComponent.showBreadcrumb();
  }
};

app.config.globalProperties.scrollToTop = function () {
  this.scrollTo(0, 0);
};

app.config.globalProperties.scrollTo = function (x, y) {
  window.scrollTo(x, y);
};

// Menu state management - re-added as global properties for backward compatibility
// Components use $root.hideMenu() and $root.showMenu() throughout the codebase
// Using reactive() so it works with Vue's reactivity system (provide/inject)
app.config.globalProperties.$menuState = reactive({ displayMenu: false });

app.config.globalProperties.showMenu = function () {
  this.$menuState.displayMenu = true;
};

app.config.globalProperties.hideMenu = function () {
  this.$menuState.displayMenu = false;
};

app.config.globalProperties.toggleMenu = function () {
  this.$menuState.displayMenu = !this.$menuState.displayMenu;
};

app.config.globalProperties.isEmailValid = function (email) {
  var re =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

app.config.globalProperties.isValidGitUrl = function (source) {
  // valid url?
  if (!isValidUrl(source)) {
    return false;
  }

  // github url?
  var isGithubUrl = parseGithubUrl(source);
  if (isGithubUrl.host !== "github.com" || typeof isGithubUrl.owner !== "string") {
    // bitbucket url?
    var isBitbucketUrl = parseBitbucketUrl(source);
    if (isBitbucketUrl.host !== "bitbucket.org" || typeof isBitbucketUrl.owner !== "string") {
      return false;
    }
  }

  return true;
};

app.config.globalProperties.getRequestUri = function (suffix) {
  return this.$requestUri + suffix;
};

app.config.globalProperties.gotoError404 = function () {
  routerInstance.push({ name: "error404", params: { locale: i18n.global.locale.value } });
};

app.config.globalProperties.gotoError500 = function () {
  routerInstance.push({ name: "error500", params: { locale: i18n.global.locale.value } });
};

app.config.globalProperties.gotoDeliveryPolicy = function () {
  routerInstance.push({ name: "deliveryPolicy", params: { locale: i18n.global.locale.value } });
};

app.config.globalProperties.gotoRefundsPolicy = function () {
  routerInstance.push({ name: "refundsPolicy", params: { locale: i18n.global.locale.value } });
};

app.config.globalProperties.gotoSelfLearningResourcesWithSelectedIds = function (ids) {
  routerInstance.push({ path: `/${i18n.global.locale.value}/self-learning-resources/${ids}` });
};

app.config.globalProperties.gotoSelfLearningResources = function () {
  routerInstance.push({
    name: "selfLearningResources",
    params: { locale: i18n.global.locale.value },
  });
  window.scrollTo(0, 0);
};

app.config.globalProperties.gotoTrainingAndMentoringProgram = function () {
  routerInstance.push({
    name: "trainingAndMentoringProgram",
    params: { locale: i18n.global.locale.value },
  });
  window.scrollTo(0, 0);
};

app.config.globalProperties.gotoProjects = function () {
  routerInstance.push({ name: "projects", params: { locale: i18n.global.locale.value } });
  window.scrollTo(0, 0);
};

app.config.globalProperties.gotoClients = function () {
  routerInstance.push({ name: "clients", params: { locale: i18n.global.locale.value } });
  window.scrollTo(0, 0);
};

app.config.globalProperties.gotoServices = function () {
  routerInstance.push({ name: "services", params: { locale: i18n.global.locale.value } });
  window.scrollTo(0, 0);
};

app.config.globalProperties.gotoPartnershipProgrammer = function () {
  routerInstance.push({
    name: "partnershipProgrammer",
    params: { locale: i18n.global.locale.value },
  });
  window.scrollTo(0, 0);
};

app.config.globalProperties.gotoPartnershipEntrepreneur = function () {
  routerInstance.push({
    name: "partnershipEntrepreneur",
    params: { locale: i18n.global.locale.value },
  });
  window.scrollTo(0, 0);
};

app.config.globalProperties.gotoContactUs = function () {
  routerInstance.push({ name: "contactUs", params: { locale: i18n.global.locale.value } });
  window.scrollTo(0, 0);
};

app.config.globalProperties.gotoJobApplication = function (id) {
  if (typeof id === "number") {
    this.$jobApplication.open = id;
  }
  routerInstance.push({ name: "jobApplication", params: { locale: i18n.global.locale.value } });
  window.scrollTo(0, 0);
};

app.config.globalProperties.gotoHome = function () {
  routerInstance.push({ name: "home", params: { locale: i18n.global.locale.value } });
  window.scrollTo(0, 0);
};

app.config.globalProperties.redirectToDefault = function () {
  this.gotoHome();
  const root = this.getRoot();
  // Vue 3 fix: Use direct header component reference
  const headerComponent = root?.$headerComponent;
  if (headerComponent) {
    headerComponent.hideBreadcrumb();
  }
};

app.config.globalProperties.responseErrorHandler = function (error) {
  // 500, 404
  if (error.response && error.response.status) {
    if (error.response.status == 500) {
      routerInstance.push({ name: "error500", params: { locale: i18n.global.locale.value } });
      throw new Error("Error 500");
    } else if (error.response.status == 404) {
      routerInstance.push({ name: "error404", params: { locale: i18n.global.locale.value } });
      throw new Error("Error 404");
    }
  }
  if (error.response && error.response.data) {
    return error.response.data;
  }
  return error;
};

app.config.globalProperties.responseMiddleware = function (response) {
  if (response.data) {
    return response.data;
  }
  return response;
};

app.config.globalProperties.init = function () {
  // The browser meet requirements?
  // HTML5 Storage/History
  // Note: currently only "History" is used
  if (typeof History !== "function" || typeof Storage !== "function") {
    alert(
      "To work properly this application requires HTML5 Web Storage feature! " +
        "Please, upgrade your browser to the latest version and try again."
    );
    return;
  }

  // start the application
  this.start();
};

app.config.globalProperties.start = function () {
  console.log("%cApplication started", "color:blue;");

  var popstate_handler = function (e) {
    // Find self-learning resources component and normalize tags
    // TODO: Replace $children usage with Vue 3 compatible pattern (provide/inject or global registry)
    // For now, this functionality is disabled to avoid Vue 3 compatibility issues
    /*
		const rootComponent = app._instance.proxy;
		if (rootComponent && rootComponent.$refs) {
			const findComponent = (component, refName) => {
				if (!component) return null;

				if (component.$refs && component.$refs[refName]) {
					return component.$refs[refName];
				}

				// Search in children - $children removed in Vue 3
				// Use provide/inject pattern instead
				return null;
			};

			const selfLearningResources = findComponent(rootComponent, 'selfLearningResources');
			if (selfLearningResources && selfLearningResources.normalizePreselectedTags) {
				selfLearningResources.normalizePreselectedTags();
			}
		}
		*/
  };

  window.onpopstate = popstate_handler.bind(this);
};

// Initialize Axios instance
app.config.globalProperties.$request = axios.create({
  baseURL: app.config.globalProperties.$requestUri,
  timeout: app.config.globalProperties.$requestTimeout,
  headers: {
    Accept: "application/json",
  },
});

// Mount app and initialize
document.addEventListener("DOMContentLoaded", () => {
  const mountedApp = app.mount("#app");

  // Initialize the app after mounting
  if (mountedApp.init) {
    mountedApp.init();
  } else {
    // Call init from global properties if not available on instance
    app.config.globalProperties.init.call(mountedApp);
  }
});
